import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { QueryKey } from '@/Config/api/queryKey';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import CircularProgress from '@/Domain/components/Shared/CircularProgress/CircularProgress';
import { buildAiCreditsFaqPath } from '@/Domain/components/Subscription/aiCreditsFaqLink';
import {
  getUsageBarColor,
  getUsageRingColor,
  getUsageTextColor,
} from '@/Domain/components/TierPlanCard/usageColor';
import {
  UpgradePlanButton,
  type UpgradeCtaSource,
} from '@/Domain/components/UpgradePlanButton/UpgradePlanButton';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useThemeStore } from '@/Domain/store/theme.store';
import { cn } from '@/Domain/utils/cn';

type Props = {
  open: boolean;
  className?: string;
  // Where this card renders, so the Upgrade CTA and the credits link attribute correctly.
  source?: UpgradeCtaSource;
};

export const TierPlanCard = ({ open, className, source = 'app_sidebar' }: Props) => {
  const { data } = useQuery<Usage>({ queryKey: [QueryKey.subscription.usage] });
  const isDark = useThemeStore((state) => state.isDarkTheme());
  const postHog = usePostHog();
  const navigate = useNavigate();
  const { t } = useTranslation('sidebar_menu');

  const openAiCreditsFaq = () => {
    postHog?.capture(PostHogEventEnum.AiCreditsFaqLinkClicked, { source });
    navigate(buildAiCreditsFaqPath());
  };

  const messagesUsed = data?.messages_used ?? 0;
  const messagesLimit = data?.messages_limit ?? 1;
  const messagesRemaining = Math.max(0, messagesLimit - messagesUsed);
  const percentUsed = messagesLimit > 0 ? Math.min(100, (messagesUsed / messagesLimit) * 100) : 0;
  const topupCredits = data?.topup_credits ?? 0;
  const severityPercent = topupCredits > 0 ? Math.min(percentUsed, 99) : percentUsed;

  // Collapsed state - show only circular progress + the icon-only upgrade CTA.
  if (!open) {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <div className="my-1.5 flex items-center justify-center">
          <CircularProgress
            maxCount={Number(messagesLimit)}
            currentCount={Number(messagesUsed)}
            color={getUsageRingColor(severityPercent, isDark)}
          />
        </div>
        <UpgradePlanButton iconOnly size="sm" className="mx-auto" source={source} />
      </div>
    );
  }

  return (
    <div className={cn('p-2.5 rounded-xl w-full bg-muted border border-border', className)}>
      {/* Header row */}
      <div className="flex items-center justify-between mb-1.5">
        <button
          type="button"
          onClick={openAiCreditsFaq}
          className="cursor-pointer text-xs font-semibold text-foreground underline decoration-muted-foreground/50 decoration-dotted underline-offset-2 transition-colors hover:decoration-foreground"
          data-testid="tier-plan-card-credits-link"
        >
          {t('free_tier.credits')}
        </button>
        <span
          className="text-xs font-medium text-muted-foreground"
          data-testid="tier-plan-card-remaining"
        >
          {topupCredits > 0
            ? t('free_tier.left_with_topup', {
                amount: messagesRemaining.toLocaleString(),
                extra: topupCredits.toLocaleString(),
              })
            : t('free_tier.left', { amount: messagesRemaining.toLocaleString() })}
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full bg-muted-foreground/15 rounded-full overflow-hidden mb-1.5">
        <div
          className={classNames(
            'h-full rounded-full transition-all duration-500 ease-out',
            getUsageBarColor(severityPercent),
          )}
          style={{ width: `${percentUsed}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="flex items-center justify-between text-[11px]">
        <span className={classNames('font-medium', getUsageTextColor(severityPercent))}>
          {Math.round(percentUsed)}% {t('free_tier.used')}
        </span>
        <span className="text-muted-foreground font-medium">{messagesLimit.toLocaleString()}</span>
      </div>

      <UpgradePlanButton size="sm" className="mt-2.5 w-full !px-2" source={source} />
    </div>
  );
};
